import React, { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Globe } from "lucide-react";

import logo from "../../assets/Logo.webp";
import logotext from "../../assets/logo-text-only.webp";
import MobileMenu from "../MobileMenu/MobileMenu";
import "./Header.css";

export default function Header() {
  const { t, i18n } = useTranslation();

  const [isScrolled, setIsScrolled] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const [aiLabMenuOpen, setAiLabMenuOpen] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleLangMenu = () => setLangMenuOpen(open => !open);
  const selectLanguage = lng => {
    i18n.changeLanguage(lng);
    setLangMenuOpen(false);
  };

  const currentEnv = process.env.REACT_APP_ENV;
  const isLoginDisabled = currentEnv === "prod" || currentEnv === "test";

  const scrollTo = id => {
    setAiLabMenuOpen(false);
    navigate(`/?scrollTo=${id}`);
  };

  const goToAIPlatform = () => {
    setAiLabMenuOpen(false);
    navigate("/ai-platform");
  };

  

  return (
    <nav className={`navbar ${isScrolled ? "scrolled" : ""}`}>

      {/* ── Mobile bar (logo + hamburger + globe on right) ── */}
      <div className="mobile-bar">
        <NavLink to="/" className="brand-link">
          <img className="logo-icon" src={logo} alt={t("header.logoAlt")} />
          <img className="logo-header logo-text" src={logotext} alt={t("header.logoTextAlt")} />
        </NavLink>
        <div className="mobile-right-actions">
          <MobileMenu />
          <div className="lang-wrapper">
            <button className="lang-btn" aria-label="Change language" onClick={toggleLangMenu}>
              <Globe size={20} />
            </button>
            {langMenuOpen && (
              <ul className="lang-menu">
                <li onClick={() => selectLanguage("en")}>English</li>
                <li onClick={() => selectLanguage("es")}>Español</li>
              </ul>
            )}
          </div>
        </div>
      </div>

      {/* ── Desktop pills ── */}
      <div className="header-pill brand-pill desktop-only">
        <NavLink to="/" className="brand-link">
          <img className="logo-icon" src={logo} alt={t("header.logoAlt")} />
          <img className="logo-header logo-text" src={logotext} alt={t("header.logoTextAlt")} />
        </NavLink>
      </div>

      <div className="header-pill nav-center-pill desktop-only">
        <ul className="navbar-options">
          <li className="header-li">
            <NavLink to="/">{t("nav.home")}</NavLink>
          </li>
          <li
            className="header-li aiLab-wrapper"
            onMouseEnter={() => setAiLabMenuOpen(true)}
            onMouseLeave={() => setAiLabMenuOpen(false)}
          >
            <button
              type="button"
              className="aiLab-btn"
              onClick={() => setAiLabMenuOpen(open => !open)}
            >
              {t("nav.aiLab")}
            </button>
            {aiLabMenuOpen && (
              <ul className="aiLab-dropdown">
                <li>
                  <button className="dropdown-link" onClick={goToAIPlatform}>
                    {t("nav.aiLabPlatform")}
                  </button>
                </li>
                <li>
                  <button className="dropdown-link" onClick={() => scrollTo("ai-lab")}>
                    {t("nav.specializedAi")}
                  </button>
                </li>
              </ul>
            )}
          </li>
          <li className="header-li">
            <NavLink to="/Contact">{t("nav.contact")}</NavLink>
          </li>
          <li className="header-li">
            <NavLink to="/AboutUs">{t("nav.aboutUs")}</NavLink>
          </li>
          <li className="header-li">
            <NavLink to="/FAQ">{t("nav.faq")}</NavLink>
          </li>
          <li className="header-li">
            <NavLink to="/Survey">Survey</NavLink>
          </li>
        </ul>
      </div>

      <div className="header-pill user-pill desktop-only">
        <div className="not-logged-wrapper">
          {process.env.REACT_APP_ENABLE_LAUNCH_APP === 'true' && (
            <NavLink
              to="/Login"
              className="auth-btn launch-btn"
              onClick={e => {
                e.preventDefault();
                window.open("https://app-dev.zenitailab.com/Login", "_blank", "noopener,noreferrer");
              }}
            >
              {t("header.launchApp")}
            </NavLink>
          )}
          <div className="lang-wrapper">
            <button className="lang-btn" aria-label="Change language" onClick={toggleLangMenu}>
              <Globe size={20} />
            </button>
            {langMenuOpen && (
              <ul className="lang-menu">
                <li onClick={() => selectLanguage("en")}>English</li>
                <li onClick={() => selectLanguage("es")}>Español</li>
              </ul>
            )}
          </div>
        </div>
      </div>

    </nav>
  );
}
